import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons/faBuilding';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons/faCircleExclamation';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { Alert, Button, Spinner } from '../ui';
import { useProviderContextStore } from '../../stores';
import type { ProviderContext } from '../../types';
import { cn } from '../../utils';
import { GlobalRating } from '../userProfile/GlobalRating';
import { formatProviderDisplayName, formatProviderRole } from './providerFormatting';

interface ProviderContextSelectorProps {
    onSelected?: () => void;
}

function contextStatusText(context: ProviderContext): string {
    if (context.can_operate) {
        return formatProviderRole(context.role);
    }

    if (context.status === 'pending') {
        return 'En revisión';
    }

    if (context.provider.status === 'suspended') {
        return 'Proveedor suspendido';
    }

    return context.status === 'removed' ? 'Membresía removida' : 'Acceso no disponible';
}

function contextDescription(context: ProviderContext): string {
    if (context.can_operate) {
        return `Puedes operar como ${formatProviderRole(context.role).toLowerCase()}.`;
    }

    if (context.status === 'pending') {
        return 'La empresa está validando tu información. Te avisarán cuando aprueben tu acceso.';
    }

    if (context.provider.status === 'suspended') {
        return 'La empresa está suspendida temporalmente. Contacta a tu administrador.';
    }

    if (context.status === 'removed') {
        return 'Tu acceso fue removido por la empresa.';
    }

    return 'Este acceso todavía no está disponible para operar.';
}

export function ProviderContextSelector({ onSelected }: ProviderContextSelectorProps) {
    const {
        contexts,
        activeContext,
        isLoading,
        error,
        loadContexts,
        selectContext,
    } = useProviderContextStore();

    const orderedContexts = useMemo(
        () => [...contexts].sort((a, b) => Number(b.can_operate) - Number(a.can_operate)),
        [contexts],
    );
    const hasPendingInvitation = orderedContexts.some((context) => context.status === 'pending' && !context.can_operate);
    const hasOperableContext = orderedContexts.some((context) => context.can_operate);

    const handleSelect = async (context: ProviderContext) => {
        if (!context.can_operate) {
            return;
        }

        const selected = await selectContext(context.transport_provider_id);
        if (selected) {
            onSelected?.();
        }
    };

    if (isLoading && contexts.length === 0) {
        return (
            <div className="flex min-h-40 items-center justify-center">
                <Spinner size="large" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {error ? (
                <Alert
                    type="error"
                    message={error.message}
                    action={{ label: 'Reintentar', onClick: () => void loadContexts() }}
                />
            ) : null}

            {orderedContexts.length === 0 ? (
                <Alert
                    type="warning"
                    message="Aún no tienes empresas activas. Acepta una invitación o solicita acceso a la empresa que te contrató."
                />
            ) : null}

            {hasPendingInvitation && !hasOperableContext ? (
                <Alert
                    type="info"
                    message="Tu solicitud quedó en revisión. La empresa debe aprobar tu acceso antes de que puedas operar."
                    action={{ label: 'Actualizar', onClick: () => void loadContexts() }}
                />
            ) : null}

            <div className="space-y-3">
                {orderedContexts.map((context) => {
                    const active = activeContext?.transport_provider_id === context.transport_provider_id;
                    const pending = context.status === 'pending' && !context.can_operate;

                    return (
                        <button
                            key={context.transport_provider_id}
                            type="button"
                            onClick={() => void handleSelect(context)}
                            disabled={!context.can_operate || isLoading}
                            className={cn(
                                'flex min-h-[88px] w-full items-start gap-3 rounded-2xl border p-4 text-left transition',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
                                active ? 'border-primary bg-primary/10 shadow-[0_14px_34px_-28px_rgba(59,130,246,0.65)]' : 'border-border bg-surface active:bg-hover',
                                pending && 'border-info/25 bg-info/8',
                                !context.can_operate && !pending && 'cursor-not-allowed opacity-75',
                                pending && 'cursor-not-allowed',
                            )}
                        >
                            <span className={cn(
                                'flex size-12 shrink-0 items-center justify-center rounded-xl bg-background text-primary',
                                pending && 'bg-info/10 text-info',
                            )}>
                                <FontAwesomeIcon icon={context.is_individual ? faUser : faBuilding} aria-hidden="true" />
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="flex flex-wrap items-center gap-2">
                                    <span className="block break-words text-base font-bold text-text">
                                        {formatProviderDisplayName(context.provider.display_name, context.is_individual)}
                                    </span>
                                    <span className={cn(
                                        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
                                        context.can_operate ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning',
                                        pending && 'bg-info/10 text-info',
                                    )}>
                                        <FontAwesomeIcon
                                            icon={context.can_operate ? faCheckCircle : (pending ? faClock : faCircleExclamation)}
                                            aria-hidden="true"
                                        />
                                        {contextStatusText(context)}
                                    </span>
                                </span>
                                <GlobalRating rating={context.provider.rating_summary ?? null} className="mt-1" />
                                <span className="mt-1.5 block text-sm leading-5 text-text-muted">
                                    {contextDescription(context)}
                                </span>
                            </span>
                            {active ? (
                                <span className="shrink-0 rounded-full bg-primary px-2 py-1 text-[11px] font-bold text-white">
                                    Activo
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            {contexts.length > 0 && activeContext === null ? (
                <Button type="button" variant="secondary" fullWidth onClick={() => void loadContexts()}>
                    Actualizar contextos
                </Button>
            ) : null}
        </div>
    );
}
